import { ClaudeAI } from "../Icons";
import { CloudProviderSettings, type CloudProviderConnection } from "./CloudProviderSettings";
import {
  canRunClaudeCloudTask,
  cloudExecutionTargetLabel,
  presentClaudeSubscriptionConnection,
  type ClaudeCloudExecutionTarget,
  type ClaudeSubscriptionConnection,
} from "./ClaudeCloudSettings.logic";
import { SettingsPageContainer } from "./settingsLayout";

type ConnectionAction = () => Promise<void>;

type DisconnectAction = () => Promise<void>;

export interface ClaudeCloudSettingsProps {
  /** Authoritative status from the cloud control plane. */
  readonly connection?: ClaudeSubscriptionConnection;
  /** The environment selected in the provider settings device picker. */
  readonly environmentKind: ClaudeCloudExecutionTarget;
  readonly environmentLabel: string;
  readonly readOnly?: boolean;
  /** The environment action is allowlisted; Settings copies its command. */
  readonly subscriptionAuthAvailable?: boolean;
  readonly authUnavailableReason?: string;
  /** Requests the allowlisted login command without receiving a credential. */
  readonly onConnect?: ConnectionAction;
  /** Requests the allowlisted logout command without receiving a credential. */
  readonly onDisconnect?: DisconnectAction;
  readonly onRetry?: () => void;
}

const DEFAULT_CONNECTION: ClaudeSubscriptionConnection = { status: "disconnected" };

export function ClaudeCloudSettings({
  connection = DEFAULT_CONNECTION,
  environmentKind,
  environmentLabel,
  readOnly = false,
  subscriptionAuthAvailable = false,
  authUnavailableReason = "This environment cannot manage Claude subscription authentication.",
  onConnect,
  onDisconnect,
  onRetry,
}: ClaudeCloudSettingsProps) {
  const presentation = presentClaudeSubscriptionConnection(connection);
  const cloudReady = canRunClaudeCloudTask({ target: environmentKind, connection });
  const cloudTarget = environmentKind === "cloud";
  const executionDescription = cloudTarget
    ? "Use the official Claude Code CLI in an isolated AgentCore runtime."
    : environmentKind === "local"
      ? "Use the official Claude Code CLI on this device."
      : "Use the official Claude Code CLI in this remote environment.";

  return (
    <CloudProviderSettings
      providerName="Claude"
      subscriptionTitle="Claude Code subscription"
      providerIcon={<ClaudeAI className="size-5" aria-hidden />}
      providerAccentClassName="border-[#d97757]/25 bg-[#d97757]/8"
      sectionId="cloud-agent"
      sectionTitle={cloudTarget ? "Claude cloud agent" : "Claude Code subscription"}
      sectionTestId="claude-cloud-settings"
      statusTestId="claude-subscription-status"
      connection={connection as CloudProviderConnection}
      presentation={presentation}
      environmentKind={environmentKind}
      environmentLabel={environmentLabel}
      executionDescription={executionDescription}
      subscriptionDescription={presentation.description}
      cloudReady={cloudReady}
      authAvailable={subscriptionAuthAvailable}
      authUnavailableReason={authUnavailableReason}
      loginCommand="claude auth login --claudeai"
      cloudWarningTitle="Connect Claude before running in Cloud"
      cloudWarningDescription="The Cloud target is selected, but no Claude Code subscription is connected yet. Local tasks remain available."
      billingDescription={
        cloudTarget ? (
          <>
            Claude model usage belongs to your Claude plan; Fabrica Cloud billing covers only the
            isolated runtime.
          </>
        ) : (
          <>
            Claude model usage belongs to your Claude plan; Fabrica adds no managed runtime charge
            for this target.
          </>
        )
      }
      readOnly={readOnly}
      {...(onConnect ? { onConnect } : {})}
      {...(onDisconnect ? { onDisconnect } : {})}
      {...(onRetry ? { onRetry } : {})}
    />
  );
}

export function ClaudeCloudSettingsPage(props: ClaudeCloudSettingsProps) {
  return (
    <SettingsPageContainer>
      <ClaudeCloudSettings {...props} />
    </SettingsPageContainer>
  );
}

export { cloudExecutionTargetLabel };
